import { useCallback, useEffect, useRef, useState } from 'react';
import { clampGhostPosition, exceedsDragThreshold } from '../utils/drag.js';

/** Any element carrying this attribute can receive a dropped habit. */
export const DROP_TARGET_ATTR = 'data-daykey';

/**
 * Pointer-event drag and drop. One implementation covers mouse, pen and touch,
 * which the native HTML5 drag API does not (it is desktop-only in practice).
 *
 * @param {(payload: any, targetKey: string) => void} onDrop
 */
export function useDragDrop(onDrop) {
  // `drag` is null when idle; otherwise it drives the floating ghost.
  const [drag, setDrag] = useState(null);
  const [overKey, setOverKey] = useState(null);

  // Mutable pointer bookkeeping — kept in a ref so listeners stay stable.
  const session = useRef(null);

  /** Which day is under the pointer right now? */
  const hitTest = (x, y) => {
    const el = document.elementFromPoint(x, y);
    const target = el && el.closest(`[${DROP_TARGET_ATTR}]`);
    return target ? target.getAttribute(DROP_TARGET_ATTR) : null;
  };

  /** Call from onPointerDown on a drag handle. */
  const startDrag = useCallback((event, payload) => {
    // Ignore right-clicks and secondary pointers.
    if (event.button != null && event.button !== 0) return;
    const rect = event.currentTarget.getBoundingClientRect();
    session.current = {
      payload,
      pointerId: event.pointerId,
      origin: { x: event.clientX, y: event.clientY },
      grabOffset: { x: event.clientX - rect.left, y: event.clientY - rect.top },
      size: { width: rect.width, height: rect.height },
      active: false,
    };
  }, []);

  useEffect(() => {
    const handleMove = (event) => {
      const s = session.current;
      if (!s || event.pointerId !== s.pointerId) return;

      const point = { x: event.clientX, y: event.clientY };
      // Wait for real movement so a tap still reads as a tap.
      if (!s.active && !exceedsDragThreshold(s.origin, point)) return;
      s.active = true;
      event.preventDefault();

      const position = clampGhostPosition(point, s.grabOffset, s.size, {
        width: window.innerWidth,
        height: window.innerHeight,
      });

      setDrag({ payload: s.payload, size: s.size, ...position });
      setOverKey(hitTest(point.x, point.y));
    };

    const finish = (event) => {
      const s = session.current;
      if (!s || (event && event.pointerId !== s.pointerId)) return;
      if (s.active && event && event.type === 'pointerup') {
        const targetKey = hitTest(event.clientX, event.clientY);
        if (targetKey) onDrop(s.payload, targetKey);
      }
      session.current = null;
      setDrag(null);
      setOverKey(null);
    };

    // passive:false so preventDefault can stop the page scrolling mid-drag.
    window.addEventListener('pointermove', handleMove, { passive: false });
    window.addEventListener('pointerup', finish);
    window.addEventListener('pointercancel', finish);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', finish);
      window.removeEventListener('pointercancel', finish);
    };
  }, [onDrop]);

  return { drag, overKey, startDrag, isDragging: drag != null };
}
